import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import WorkIcon from "@mui/icons-material/Work";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LeaveIcon from "@mui/icons-material/EventAvailable";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import BasicInformationForm from "./tabs/BasicInformationForm";
import PermissionsTab from "./tabs/PermissionsForm";
import EmploymentDetailsTab from "./tabs/EmploymentDetailsForm";
import SalaryInformationTab from "./tabs/SalaryInfoForm";
import LeaveInformationTab from "./tabs/LeaveInfoForm";
import ChallansTab from "./tabs/ChallansForm";
import MasterViewController from "../../redux/actions/masterApiActions";
import EmployeeProfileVeiwController from "../../redux/actions/EmployeeProfileApiAction";
import { getUserInfoFromToken } from "../../utils/authUtils";
import defaultPermissionsData from "../../JsonFile/PermisssionJsonfile.json";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx, BRAND } from "../../components/auth/authTheme";

const DRAWER_WIDTH = { xs: "100%", sm: 640, md: 760 };

const steps = [
  { label: "Basic Information", icon: <InfoIcon fontSize="small" /> },
  { label: "Employment Details", icon: <WorkIcon fontSize="small" /> },
  { label: "Salary Details", icon: <MonetizationOnIcon fontSize="small" /> },
  { label: "Leave Details", icon: <LeaveIcon fontSize="small" /> },
  { label: "Challans", icon: <ReceiptIcon fontSize="small" /> },
  { label: "Permissions", icon: <CheckCircleIcon fontSize="small" /> },
];

const STEP_HINTS = [
  "Enter the employee's name and work email address.",
  "Set department, designation, and employment settings.",
  "Add salary structure and bank details.",
  "Configure leave balances and attendance rules.",
  "Select applicable tax challans.",
  "Choose what this employee can access in the app.",
];

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_ATTENDANCE_TYPE = "manual";

const resolveCompanyId = (companyRef) => {
  if (companyRef == null || companyRef === "") return null;
  if (typeof companyRef === "number" && !Number.isNaN(companyRef)) return companyRef;
  if (typeof companyRef === "string") {
    const trimmed = companyRef.trim();
    return trimmed === "" ? null : trimmed;
  }
  if (typeof companyRef === "object" && companyRef.id != null) {
    return String(companyRef.id);
  }
  return null;
};

const normalizeOvertimeEligible = (value) => {
  if (value === "Yes" || value === true) return true;
  if (value === "No" || value === false) return false;
  return false;
};

const buildEmployeeSubmitPayload = (
  values,
  { challan, permissions, userType }
) => {
  const payload = {
    id: values.id,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    mobile_number: values.mobile_number,
    password: values.password,
    profile: {
      joining_date: values.joining_date,
      department: values.department,
      designation: values.designation,
      employment_type: values.employment_type,
      work_location: values.work_location,
      reporting_manager: values.reporting_manager,
      employee_status: values.employee_status,
      job_description: values.job_description,
      shift_timing: values.shift_timing,
      weekly_off: Array.isArray(values.weekly_off)
        ? values.weekly_off.join(", ")
        : values.weekly_off,
      working_hours: values.working_hours,
    },
    employee_salary: {
      employee: values.id,
      basic_salary: values.basic_salary,
      other_allowances: values.other_allowances,
      deductions: values.deductions,
      hra: values.hra,
      bonus: values.bonus,
      bank_name: values.bank_name,
      account_number: values.account_number,
      ifsc_code: values.ifsc_code,
      pan_number: values.pan_number,
      pf_number: values.pf_number,
      esi_number: values.esi_number,
      challans: challan || [],
    },
    leave_balance: {
      paid_leave_balance: values.paid_leave_balance,
      unpaid_leave_balance: values.unpaid_leave_balance,
      leave_approval_managers: values.leave_approval_managers,
      leave_year:
        values.leave_year !== "" && values.leave_year != null
          ? Number(values.leave_year)
          : CURRENT_YEAR,
      attendance_type: values.attendance_type || DEFAULT_ATTENDANCE_TYPE,
      overtime_eligible: normalizeOvertimeEligible(values.overtime_eligible),
      casual_leave_balance: values.casual_leave_balance,
      sick_leave_balance: values.sick_leave_balance,
      maternity_leave_balance: values.maternity_leave_balance,
      marriage_leave_balance: values.marriage_leave_balance,
      compensatory_off_leave_balance: values.compensatory_off_leave_balance,
    },
    permissions: permissions.length > 0 ? { permissions: permissions } : {},
  };

  // Only admins choose company explicitly; employers rely on backend request.user.company.
  if (userType === "Admin") {
    const resolvedCompanyId = resolveCompanyId(values.company_id);
    if (resolvedCompanyId != null) {
      payload.company_id = resolvedCompanyId;
    }
  }

  return payload;
};

const stepValidationSchemas = [
  Yup.object({
    first_name: Yup.string().required("First Name is required"),
    password: Yup.string()
      .nullable()
      .test(
        "password-required",
        "Password is required when registering with a mobile number",
        function (value) {
          const { email } = this.parent;
          const isEdit = this.options.context?.isEdit;
          if (!isEdit && !email && !value) {
            return false;
          }
          return true;
        }
      )
      .test(
        "password-min",
        "Password must be at least 6 characters",
        function (value) {
          if (value && value.length < 6) return false;
          return true;
        }
      ),
    email: Yup.string()
      .email("Invalid email")
      .nullable()
      .test(
        "email-or-mobile",
        "Email or Mobile Number is required",
        function (value) {
          return !!(value || this.parent.mobile_number);
        }
      ),
    mobile_number: Yup.string()
      .matches(/^[0-9]{10}$/, "Enter a valid 10-digit phone number")
      .nullable()
      .test(
        "email-or-mobile",
        "Email or Mobile Number is required",
        function (value) {
          return !!(value || this.parent.email);
        }
      ),
  }),
  Yup.object({
    joining_date: Yup.string().required("Joining Date is required"),
    employment_type: Yup.string().required("Employment Type is required"),
    work_location: Yup.string().required("Work Location is required"),
    employee_status: Yup.string().required("Employee Status is required"),
    weekly_off: Yup.array().min(1, "Select at least one day off").required("Weekly Off is required")
  }),
  Yup.object({
    basic_salary: Yup.number()
      .min(0, "Must be non-negative")
      .required("Basic Salary is required")
  }),
  Yup.object({
    // leave_approval_managers: Yup.array()
    //   .of(Yup.string())
    //   .min(1, "Select at least one manager")
    //   .required("Leave Approval Manager is required"),
    //   leave_year: Yup.number().min(0, "Must be non-negative").required("Leave Year is required"),
    //   attendance_type: Yup.string()
    //     .required("Attendance type is required"),
    //  overtime_eligible: Yup.string()
    // .oneOf(["Yes", "No"], "Select Yes or No")
    // .required("Overtime eligibility is required"),

    casual_leave_balance: Yup.number()
      .min(0, "Must be non-negative")
      .required("Casual Leave Balance is required"),
    sick_leave_balance: Yup.number()
      .min(0, "Must be non-negative")
      .required("Sick Leave Balance is required"),
    maternity_leave_balance: Yup.number()
      .min(0, "Must be non-negative")
      .required("Maternity Leave Balance is required"),
    compensatory_off_leave_balance: Yup.number()
      .min(0, "Must be non-negative")
      .required("Compensatory Off Leave Balance is required")
  }),
  Yup.object({}),
];

const AddEmployeeModal = ({ open, handleClose, initialData = null }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [permissions, setPermissions] = useState([]);
  const [challan, setChallan] = useState([]);
  const [employeeId, setEmployeeId] = useState(initialData?.id || null);
  const dispatch = useDispatch();
  const isEdit = Boolean(initialData?.id);
  const { getPermissionsModules, getRoles } = MasterViewController();
  const { getChallanMaster } = MasterViewController();
  const { addEmployee, updateEmployee } = EmployeeProfileVeiwController();
  const { permission_modules, challans: masterChallans } = useSelector(
    (state) => state.master
  );
  const challans = useMemo(
    () => (Array.isArray(masterChallans) ? masterChallans : []),
    [masterChallans]
  );
  const { isLoding } = useSelector((state) => state.employeeProfile);
  const contentRef = useRef(null);
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  // const [submittingLoader, setSubmittingLoader] = useState(false);

  // useEffect(() => {
  //   if (open) {
  //     setActiveStep(0);
  //     setPermissions(initialData?.permissions?.permissions || []); // or [] for add
  //     setEmployeeId(initialData?.id || null);
  //     console.log("Modal opened, initializing form...");
  //   }
  // }, [open]);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setEmployeeId(initialData?.id || null);
      setChallan(
        Array.isArray(initialData?.employee_salary?.challans)
          ? initialData.employee_salary.challans
          : []
      );
    }
  }, [open, initialData?.id]);
  const formikRef = useRef();
      useEffect(() => {
      const { userType, userId } = getUserInfoFromToken();
      setUserType(userType);
      setUserId(userId);
    }, []);


  const { roles } = useSelector((state) => state.master);
  
  // Calculate basePermissions for the Employee role
  const [basePermissions, setBasePermissions] = useState([]);

  useEffect(() => {
    if (!open || !userType) return;
    dispatch(getRoles());
    dispatch(getPermissionsModules(userType));
  }, [dispatch, open, userType]);

  useEffect(() => {
    if (!open) return;
    dispatch(getChallanMaster());
  }, [dispatch, open]);

  useEffect(() => {
    if (roles && roles.length > 0 && permission_modules?.length) {
      const employeeRole = roles.find(r => r.role_name.toLowerCase() === "employee");
      const rolePerms = employeeRole?.permissions || [];
      
      const calculatedBase = permission_modules.map((mod) => {
        const found = rolePerms.find((p) => p.module === mod.key);
        let enabled = false;
        if (found) {
           enabled = mod.actions.some(act => found[act.key]);
        }
        const row = {
          module: mod.key,
          moduleLabel: mod.label,
          enabled,
          ...(found || {})
        };
        return row;
      });
      setBasePermissions(calculatedBase);
    }
  }, [roles, permission_modules]);

  useEffect(() => {
    if (!open || !permission_modules?.length) return;

    if (initialData?.permissions?.permissions?.length) {
      const patchedData = basePermissions.map((masterItem) => {
        const incomingItem = initialData.permissions.permissions.find(
          (item) => item.module === masterItem.module
        );
        return incomingItem ? { ...masterItem, ...incomingItem } : masterItem;
      });
      setPermissions(patchedData);
    } else if (basePermissions?.length) {
      setPermissions(basePermissions.map(p => ({...p})));
    }
  }, [open, initialData?.id, basePermissions, permission_modules?.length]);

  const handleNext = async (formik) => {
    const errors = await formik.validateForm();
    formik.setTouched(
      Object.keys(stepValidationSchemas[activeStep].fields).reduce(
        (acc, field) => {
          acc[field] = true;
          return acc;
        },
        {}
      )
    );

    if (activeStep === 0 && userType === "Admin" && !formik.values.company_id) {
      formik.setFieldError("company_id", "Company is required");
      formik.setFieldTouched("company_id", true);
      return;
    }

    if (Object.keys(errors).length === 0) {
      setActiveStep((prev) => prev + 1);
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setTimeout(() => {
        const firstErrorKey = Object.keys(errors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }, 100);
    }
  };

  const handleStepIconClick = async (targetStepIndex, formik) => {
    if (targetStepIndex < activeStep) {
      setActiveStep(targetStepIndex);
      return;
    }

    // Prevent jumping more than one step ahead
    if (targetStepIndex > activeStep + 1) {
      return;
    }

    const errors = await formik.validateForm();

    formik.setTouched(
      Object.keys(stepValidationSchemas[activeStep].fields).reduce(
        (acc, field) => {
          acc[field] = true;
          return acc;
        },
        {}
      )
    );

    if (activeStep === 0 && userType === "Admin" && !formik.values.company_id) {
      formik.setFieldError("company_id", "Company is required");
      formik.setFieldTouched("company_id", true);
      return;
    }

    if (Object.keys(errors).length === 0) {
      setActiveStep(targetStepIndex);
    } else {
      setTimeout(() => {
        const firstErrorKey = Object.keys(errors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }, 100);
    }
  };

  const handleFinalSubmit = async (values, setSubmitting) => {
    setSubmitting(true);
    // setSubmittingLoader(true)

    try {
      const combinedData = buildEmployeeSubmitPayload(values, {
        challan,
        permissions,
        userType,
      });

      let success = false;
      if (isEdit) {
        success = await dispatch(updateEmployee(values.id, combinedData));
      } else {
        success = await dispatch(addEmployee(combinedData));
      }

      if (success) {
        handleClose(); // âœ… Only called when success
      }
    } catch (error) {
      console.error("Failed to submit combined data:", error);
    } finally {
      setSubmitting(false);
    }
  };
  const handleCloseDrawer = () => {
    setActiveStep(0);
    handleClose();
  };

  const initialValues = useMemo(
    () => ({
      id: initialData?.id || null,
      company_id: initialData?.company?.id || "",
      first_name: initialData?.first_name || "",
      last_name: initialData?.last_name || "",
      email: initialData?.email || "",
      mobile_number: initialData?.mobile_number || "",
      password: "",

      department: initialData?.profile_data?.department || "",
      designation: initialData?.profile_data?.designation || "",
      joining_date: initialData?.profile_data?.joining_date || "",
      employment_type: initialData?.profile_data?.employment_type || "",
      work_location: initialData?.profile_data?.work_location || "",
      reporting_manager: initialData?.profile_data?.reporting_manager?.id || "",
      employee_status: initialData?.profile_data?.employee_status || "",
      job_description: initialData?.profile_data?.job_description || "",
      shift_timing: initialData?.profile_data?.shift_timing || "",
      weekly_off: initialData?.profile_data?.weekly_off
        ? initialData.profile_data.weekly_off.split(",").map((day) => day.trim())
        : [],
      working_hours: initialData?.profile_data?.working_hours || "",

      basic_salary: initialData?.employee_salary?.basic_salary || "",
      other_allowances: initialData?.employee_salary?.other_allowances || "",
      deductions: initialData?.employee_salary?.deductions || "",
      bank_name: initialData?.employee_salary?.bank_name || "",
      account_number: initialData?.employee_salary?.account_number || "",
      hra: initialData?.employee_salary?.hra || "",
      bonus: initialData?.employee_salary?.bonus || "",
      ifsc_code: initialData?.employee_salary?.ifsc_code || "",
      pan_number: initialData?.employee_salary?.pan_number || "",
      pf_number: initialData?.employee_salary?.pf_number || "",
      esi_number: initialData?.employee_salary?.esi_number || "",

      paid_leave_balance: initialData?.leave_balance?.paid_leave_balance || 0,
      unpaid_leave_balance: initialData?.leave_balance?.unpaid_leave_balance || 0,
      leave_approval_managers: initialData?.leave_balance?.leave_approval_managers
        ? initialData.leave_balance.leave_approval_managers.map(
            (manager) => manager.id
          )
        : [],
      leave_year: initialData?.leave_balance?.leave_year || CURRENT_YEAR,
      attendance_type:
        initialData?.leave_balance?.attendance_type || DEFAULT_ATTENDANCE_TYPE,
      overtime_eligible:
        initialData?.leave_balance?.overtime_eligible === true
          ? "Yes"
          : initialData?.leave_balance?.overtime_eligible === false
          ? "No"
          : "No",
      casual_leave_balance: initialData?.leave_balance?.casual_leave_balance || 0,
      sick_leave_balance: initialData?.leave_balance?.sick_leave_balance || 0,
      maternity_leave_balance:
        initialData?.leave_balance?.maternity_leave_balance || 0,
      marriage_leave_balance:
        initialData?.leave_balance?.marriage_leave_balance || 0,
      compensatory_off_leave_balance:
        initialData?.leave_balance?.compensatory_off_leave_balance || 0,
    }),
    [initialData?.id]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!open) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={stepValidationSchemas[activeStep]}
      enableReinitialize
      context={{ isEdit }}
      onSubmit={(values, { setSubmitting }) => setSubmitting(false)}
    >
      {(formikProps) => {
        const { isSubmitting, values, setSubmitting } = formikProps;
        const isSaving = isSubmitting || isLoding;
        const stepProgress = ((activeStep + 1) / steps.length) * 100;

        const renderFooter = () => {
          if (isMobile) {
            return (
              <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                {activeStep > 0 && (
                  <Button
                    variant="outlined"
                    disabled={isSaving}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                    sx={{
                      minWidth: 96,
                      minHeight: 48,
                      borderRadius: "12px",
                      borderColor: BRAND.border,
                      color: BRAND.navy,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Back
                  </Button>
                )}
                {activeStep < steps.length - 1 ? (
                  <Button
                    type="button"
                    variant="contained"
                    disabled={isSaving}
                    onClick={() => handleNext(formikProps)}
                    fullWidth
                    sx={{
                      ...primaryButtonSx,
                      flex: 1,
                      minHeight: 52,
                      borderRadius: "14px",
                      fontSize: "1rem",
                      boxShadow: "0 4px 16px rgba(230, 90, 0, 0.28)",
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="contained"
                    disabled={isSaving}
                    onClick={async () => {
                      const errors = await formikProps.validateForm();
                      if (Object.keys(errors).length === 0) {
                        handleFinalSubmit(values, setSubmitting);
                      } else {
                        formikProps.setTouched(
                          Object.keys(stepValidationSchemas[activeStep]?.fields || {}).reduce(
                            (acc, field) => { acc[field] = true; return acc; }, {}
                          )
                        );
                        setTimeout(() => {
                          const firstErrorKey = Object.keys(errors)[0];
                          const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
                          if (errorElement) {
                            errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                            errorElement.focus();
                          }
                        }, 100);
                      }
                    }}
                    fullWidth
                    sx={{
                      ...primaryButtonSx,
                      flex: 1,
                      minHeight: 48,
                      borderRadius: "12px",
                      "&.Mui-disabled": {
                        bgcolor: isSaving ? BRAND.orange : undefined,
                        color: isSaving ? "#fff" : undefined,
                        opacity: isSaving ? 0.85 : undefined,
                      },
                    }}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {isSaving
                      ? isEdit
                        ? "Updating..."
                        : "Creating..."
                      : isEdit
                        ? "Save changes"
                        : "Create employee"}
                  </Button>
                )}
              </Box>
            );
          }

          return (
            <>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCloseDrawer}
                disabled={isSaving}
                sx={{
                  borderColor: BRAND.border,
                  color: BRAND.orange,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                disabled={activeStep === 0 || isSaving}
                onClick={() => setActiveStep((prev) => prev - 1)}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  onClick={() => handleNext(formikProps)}
                  sx={primaryButtonSx}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  disabled={isSaving}
                  onClick={() => handleFinalSubmit(values, setSubmitting)}
                  sx={{
                    ...primaryButtonSx,
                    minWidth: 120,
                    "&.Mui-disabled": {
                      bgcolor: isSaving ? BRAND.orange : undefined,
                      color: isSaving ? "#fff" : undefined,
                      opacity: isSaving ? 0.85 : undefined,
                    },
                  }}
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  {isSaving
                    ? isEdit
                      ? "Updating..."
                      : "Creating..."
                    : isEdit
                      ? "Update"
                      : "Submit"}
                </Button>
              )}
            </>
          );
        };

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={open}
              onClose={handleCloseDrawer}
              width={DRAWER_WIDTH}
              title={isEdit ? "Edit Employee" : "Add Employee"}
              subtitle={steps[activeStep]?.label}
              headerMeta={
                isMobile ? `Step ${activeStep + 1} of ${steps.length}` : undefined
              }
              progress={isMobile ? stepProgress : undefined}
              footer={renderFooter()}
            >
              {isMobile && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        bgcolor: "var(--brand-orange-light, rgba(230,90,0,0.12))",
                        color: "var(--brand-orange, #E65A00)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {steps[activeStep]?.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: "1.0625rem",
                          fontWeight: 700,
                          color: BRAND.navy,
                          lineHeight: 1.3,
                          mb: 0.5,
                        }}
                      >
                        {steps[activeStep]?.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          color: BRAND.muted,
                          lineHeight: 1.45,
                        }}
                      >
                        {STEP_HINTS[activeStep]}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  display: isMobile ? "none" : "block",
                  width: "100%",
                  overflowX: "auto",
                  mb: 3,
                  pb: 1,
                  "::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                <Stepper
                  alternativeLabel
                  activeStep={activeStep}
                  sx={{
                    minWidth: { xs: 600, sm: "100%" },
                    "& .MuiStepConnector-line": {
                      borderColor: BRAND.border,
                    },
                    "& .MuiStepIcon-root": {
                      color: "#CBD5E1",
                      "&.Mui-active": { color: BRAND.orange },
                      "&.Mui-completed": { color: BRAND.orange },
                    },
                    "& .MuiStepLabel-label": {
                      fontSize: "0.75rem",
                      color: BRAND.muted,
                      "&.Mui-active": {
                        color: BRAND.navy,
                        fontWeight: 600,
                      },
                      "&.Mui-completed": {
                        color: BRAND.navy,
                        fontWeight: 500,
                      },
                    },
                  }}
                >
                  {steps.map((step, index) => (
                    <Step
                      key={step.label}
                      onClick={() => handleStepIconClick(index, formikProps)}
                      sx={{ cursor: "pointer", minWidth: 100 }}
                    >
                      <StepLabel>{step.label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              <Box
                ref={contentRef}
                sx={
                  isMobile
                    ? {
                        bgcolor: "#fff",
                        borderRadius: "16px",
                        border: `1px solid ${BRAND.border}`,
                        boxShadow: "0 2px 12px rgba(26, 43, 74, 0.05)",
                        p: 2,
                        width: "100%",
                        alignSelf: "flex-start",
                      }
                    : undefined
                }
              >
                {activeStep === 0 && (
                  <BasicInformationForm formik={formikProps} isEdit={isEdit} isMobile={isMobile} />
                )}
                {activeStep === 1 && (
                  <EmploymentDetailsTab
                    formik={formikProps}
                    employeeId={employeeId}
                  />
                )}
                {activeStep === 2 && (
                  <SalaryInformationTab
                    formik={formikProps}
                    employeeId={employeeId}
                  />
                )}
                {activeStep === 3 && (
                  <LeaveInformationTab
                    formik={formikProps}
                    employeeId={employeeId}
                  />
                )}
                {activeStep === 4 && (
                  <ChallansTab
                    challans={challans}
                    selectedChallan={challan}
                    setSelectedChallan={setChallan}
                  />
                )}
                {activeStep === 5 && (
                  <PermissionsTab
                    formik={formikProps}
                    modulesData={permission_modules}
                    permissions={permissions}
                    setPermissions={setPermissions}
                    basePermissions={basePermissions}
                  />
                )}
              </Box>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddEmployeeModal;

//   const [activeStep, setActiveStep] = useState(0);
